// CHANGE: Removed all debug console statements for production
// Password reset modal for administrators
// Displays the temporary password after reset
// CHANGE: Added click-outside-to-close and Escape key handlers
// CHANGE: Added ARIA attributes for accessibility

import { createPortal } from 'react-dom';
import { getPortalRoot } from '../../lib/portalUtils';
import { X, Copy, Check, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';

interface PasswordResetModalProps {
  username: string;
  tempPassword: string;
  onClose: () => void;
}

export function PasswordResetModal({ username, tempPassword, onClose }: PasswordResetModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="password-reset-title">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 shrink-0">
          <h3 id="password-reset-title" className="text-xl font-bold text-gray-900">Mot de passe temporaire</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Important !</p>
              <p>Ce mot de passe temporaire ne sera affiché qu'une seule fois. Assurez-vous de le communiquer à l'utilisateur de manière sécurisée.</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Utilisateur
            </label>
            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
              <span className="font-mono text-gray-900">{username}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mot de passe temporaire
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="font-mono text-lg text-gray-900 font-semibold">{tempPassword}</span>
              </div>
              <button
                onClick={handleCopy}
                className="px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center gap-2"
                title="Copier le mot de passe"
              >
                {copied ? (
                  <>
                    <Check size={20} />
                    <span>Copié</span>
                  </>
                ) : (
                  <>
                    <Copy size={20} />
                    <span>Copier</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>À la prochaine connexion</strong>, l'utilisateur devra obligatoirement changer ce mot de passe temporaire.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200"
          >
            J'ai noté le mot de passe
          </button>
        </div>
      </div>
    </div>,
    getPortalRoot()
  );
}
